import React, { useState } from 'react';
import { Box, Button, Flex, FormControl, FormLabel, Heading, Input, Text } from '@chakra-ui/react';
import { getFeesBreakdownReport } from 'api/fees-breakdown-reports';
import LoadingErrorWrapper from 'components/LoadingErrorWrapper/LoadingErrorWrapper';

const toRFC3339 = (localDateTime) => {
  if (!localDateTime) return '';

  // datetime-local gives "YYYY-MM-DDTHH:mm"; convert to a full ISO/RFC3339 string.
  return new Date(localDateTime).toISOString();
};

const FeesBreakdownReports = () => {
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [entityIds, setEntityIds] = useState('');

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const parsedEntityIds = entityIds
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);

      const response = await getFeesBreakdownReport(toRFC3339(startTime), toRFC3339(endTime), parsedEntityIds);

      setReport(response?.data?.data ?? null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
      setHasFetched(true);
    }
  };

  const isGenerateDisabled = !startTime || !endTime || loading;

  return (
    <Box maxWidth='100%' p={4} borderRadius='lg' boxShadow='sm' bg='white'>
      <Heading as='h1' size='md' mb={4} mt={4} color='gray.700'>
        Fees Breakdown Reports
      </Heading>

      <Flex gap={4} flexWrap='wrap' align='flex-end' mb={4}>
        <FormControl isRequired maxW='260px'>
          <FormLabel fontSize='sm' color='gray.700'>
            Start time
          </FormLabel>
          <Input type='datetime-local' value={startTime} onChange={(e) => setStartTime(e.target.value)} size='sm' />
        </FormControl>

        <FormControl isRequired maxW='260px'>
          <FormLabel fontSize='sm' color='gray.700'>
            End time
          </FormLabel>
          <Input type='datetime-local' value={endTime} onChange={(e) => setEndTime(e.target.value)} size='sm' />
        </FormControl>

        <FormControl maxW='320px'>
          <FormLabel fontSize='sm' color='gray.700'>
            Entity IDs (optional)
          </FormLabel>
          <Input
            type='text'
            placeholder='Comma separated, empty for all entities'
            value={entityIds}
            onChange={(e) => setEntityIds(e.target.value)}
            size='sm'
          />
        </FormControl>

        <Button colorScheme='blue' size='sm' onClick={fetchReport} isDisabled={isGenerateDisabled}>
          Generate Report
        </Button>
      </Flex>

      <LoadingErrorWrapper isLoading={loading} errorTitle='Error fetching Fees Breakdown Report' error={error}>
        {hasFetched && (
          <Box mt={4} p={4} borderRadius='lg' border='1px solid' borderColor='gray.200' overflowX='auto'>
            {report ? (
              <Box as='pre' fontSize='sm' color='gray.700' whiteSpace='pre-wrap'>
                {JSON.stringify(report, null, 2)}
              </Box>
            ) : (
              <Text fontSize='sm' color='gray.500'>
                No data found for the selected filters.
              </Text>
            )}
          </Box>
        )}
      </LoadingErrorWrapper>
    </Box>
  );
};

export default FeesBreakdownReports;
